<template>
  <div v-if="open" class="um-backdrop" @click.self="close">
    <div class="um-dialog" role="dialog" aria-modal="true" :aria-label="title">
      <div class="um-head">
        <div class="vp-min-w-0">
          <h2 class="um-title">{{ title }}</h2>
          <div class="um-subtitle">{{ subtitle }}</div>
        </div>
        <button type="button" class="um-close" :aria-label="$t('common.actions.close')" @click="close">
          <i class="ph-duotone ph-x"></i>
        </button>
      </div>

      <div v-if="loading" class="um-loading">
        <span class="spinner-border spinner-border-sm me-2"></span>{{ $t('common.loading') }}
      </div>

      <form v-else class="um-body" @submit.prevent="save">
        <div class="um-grid">
          <div>
            <label class="um-label" for="um-email">
              {{ $t('users.fields.email') }} <span class="um-req">*</span>
            </label>
            <input
              id="um-email"
              v-model="form.userName"
              type="email"
              class="um-input"
              :class="{ 'is-invalid': !!errors.userName }"
              :placeholder="$t('users.modal.emailPlaceholder')"
            />
            <div v-if="errors.userName" class="um-error">{{ errors.userName }}</div>
          </div>

          <div>
            <label class="um-label" for="um-display">{{ $t('users.fields.displayName') }}</label>
            <input
              id="um-display"
              v-model="form.displayUserName"
              type="text"
              class="um-input"
              :placeholder="$t('users.modal.displayNamePlaceholder')"
            />
          </div>

          <div>
            <label class="um-label" for="um-first">{{ $t('users.fields.firstName') }}</label>
            <input
              id="um-first"
              v-model="form.name"
              type="text"
              class="um-input"
              :placeholder="$t('users.placeholders.firstName')"
            />
          </div>

          <div>
            <label class="um-label" for="um-last">{{ $t('users.fields.lastName') }}</label>
            <input
              id="um-last"
              v-model="form.surname"
              type="text"
              class="um-input"
              :placeholder="$t('users.placeholders.lastName')"
            />
          </div>

          <div>
            <label class="um-label" for="um-phone">{{ $t('users.fields.phone') }}</label>
            <input
              id="um-phone"
              v-model="form.phoneNumber"
              type="tel"
              class="um-input"
              :placeholder="$t('users.placeholders.phone')"
            />
          </div>
        </div>

        <!-- Resetting a password only makes sense once the account exists. -->
        <div v-if="isEditMode" class="um-reset">
          <div class="um-reset-text">
            <div class="um-reset-title">{{ $t('users.passwordSetup.title') }}</div>
            <div class="um-reset-note">{{ $t('users.passwordSetup.description') }}</div>
          </div>
          <button type="button" class="um-reset-btn" :disabled="sendingEmail" @click="sendPasswordSetupEmail">
            <span v-if="sendingEmail" class="spinner-border spinner-border-sm"></span>
            <i v-else class="ph-duotone ph-envelope-simple"></i>
            {{ $t('users.passwordSetup.sendEmail') }}
          </button>
        </div>

        <button
          type="button"
          class="um-toggle-row"
          :aria-pressed="form.isActive"
          @click="form.isActive = !form.isActive"
        >
          <span class="um-switch" :class="{ 'is-on': form.isActive }"><i></i></span>
          <span class="vp-min-w-0">
            <span class="um-toggle-label">{{ activeLabel }}</span>
            <span class="um-toggle-note">{{ activeNote }}</span>
          </span>
        </button>
      </form>

      <div class="um-foot">
        <button type="button" class="um-btn" @click="close">{{ $t('common.actions.cancel') }}</button>
        <button type="button" class="um-btn is-primary" :disabled="saving || loading" @click="save">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          {{ cta }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
// 1. Vue core
import { ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

// 2. Services
import userService from '@/services/userService'

// 3. Constants
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const props = defineProps({
  open: { type: Boolean, default: false },
  userId: { type: String, default: null }
})

const emit = defineEmits(['close', 'saved'])

const store = useStore()
const { t } = useI18n()

const loading = ref(false)
const saving = ref(false)
const sendingEmail = ref(false)

const form = reactive({
  userName: '',
  displayUserName: '',
  name: '',
  surname: '',
  phoneNumber: '',
  isActive: true
})

const errors = reactive({ userName: '' })

const isEditMode = computed(() => !!props.userId)
const title = computed(() => (isEditMode.value ? t('users.modal.editTitle') : t('users.modal.addTitle')))
const subtitle = computed(() => (isEditMode.value ? t('users.modal.editSubtitle') : t('users.modal.addSubtitle')))
const cta = computed(() => (isEditMode.value ? t('users.modal.editCta') : t('users.modal.addCta')))
const activeLabel = computed(() => (form.isActive ? t('users.status.active') : t('users.status.inactive')))
const activeNote = computed(() => (form.isActive ? t('users.modal.activeNote') : t('users.modal.inactiveNote')))

const resetForm = () => {
  form.userName = ''
  form.displayUserName = ''
  form.name = ''
  form.surname = ''
  form.phoneNumber = ''
  form.isActive = true
  errors.userName = ''
}

const loadUser = async () => {
  loading.value = true
  try {
    const data = await userService.getTenantUserById(props.userId)
    // The API answers with `email` on read but expects `userName` on write.
    form.userName = data.userName || data.email || ''
    form.displayUserName = data.displayUserName || ''
    form.name = data.name || ''
    form.surname = data.surname || ''
    form.phoneNumber = data.phoneNumber || ''
    form.isActive = data.isActive !== false
  } catch {
    store.dispatch('notify', { message: t('users.errors.loadFailed'), type: 'error' })
    emit('close')
  } finally {
    loading.value = false
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    resetForm()
    if (props.userId) loadUser()
  },
  { immediate: true }
)

const close = () => emit('close')

const validate = () => {
  const email = form.userName.trim()
  if (!email) {
    errors.userName = t('users.validation.usernameRequired')
    return false
  }
  if (!EMAIL_PATTERN.test(email)) {
    errors.userName = t('users.validation.emailInvalid')
    return false
  }
  errors.userName = ''
  return true
}

/** The API reports validation detail in a few shapes; take the most specific one present. */
const messageFor = (err, fallback) => {
  const apiError = err?.response?.data?.error
  if (apiError?.validationErrors?.length) {
    return apiError.validationErrors.map(v => v.message).join(' ')
  }
  if (apiError?.details) return apiError.details.replace(/\r\n/g, ' ').trim()
  if (apiError?.message) return apiError.message
  return fallback
}

const save = async () => {
  if (!validate()) return

  saving.value = true
  try {
    // Username and email are the same field for supplier users.
    const email = form.userName.trim()
    const payload = { ...form, userName: email, email }

    if (isEditMode.value) {
      await userService.updateUser(props.userId, payload)
      store.dispatch('notify', { message: t('users.updateSuccess'), type: 'success' })
    } else {
      await userService.createUser(payload)
      store.dispatch('notify', { message: t('users.createSuccess'), type: 'success' })
    }

    emit('saved')
    emit('close')
  } catch (err) {
    const fallback = isEditMode.value ? t('users.errors.updateFailed') : t('users.errors.createFailed')
    store.dispatch('notify', { message: messageFor(err, fallback), type: 'error' })
  } finally {
    saving.value = false
  }
}

const sendPasswordSetupEmail = async () => {
  if (!validate()) return

  sendingEmail.value = true
  try {
    await userService.sendPasswordSetupEmail(form.userName.trim())
    store.dispatch('notify', { message: t('users.passwordSetup.emailSent'), type: 'success' })
  } catch (err) {
    store.dispatch('notify', { message: messageFor(err, t('users.passwordSetup.emailError')), type: 'error' })
  } finally {
    sendingEmail.value = false
  }
}
</script>

<style scoped>
.um-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(16, 42, 79, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 20px;
  z-index: 1060;
  overflow: auto;
}

.um-dialog {
  background: var(--vp-surface);
  border-radius: 14px;
  box-shadow: 0 30px 70px rgba(8, 22, 45, 0.35);
  width: 100%;
  max-width: 640px;
}

.um-head {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid var(--vp-gray-200);
}

.um-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--vp-heading-color);
  margin: 0;
}

.um-subtitle {
  font-size: 0.78rem;
  color: var(--vp-gray-500);
  margin-top: 2px;
}

.um-close {
  margin-left: auto;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid var(--vp-gray-300);
  background: var(--vp-surface);
  color: var(--vp-muted-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.um-close:hover {
  background: var(--vp-gray-200);
}

.um-loading {
  padding: 40px 20px;
  text-align: center;
  color: var(--vp-gray-500);
  font-size: 0.85rem;
}

.um-body {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.um-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.um-label {
  font-size: 0.79rem;
  font-weight: 700;
  color: var(--vp-body-color);
  display: block;
  margin-bottom: 6px;
}

.um-req {
  color: var(--vp-coral);
}

.um-input {
  width: 100%;
  border: 1px solid var(--vp-gray-300);
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 0.85rem;
  color: var(--vp-body-color);
  outline: none;
  background: #fbfcfe;
}

.um-input:focus {
  border-color: var(--vp-accent);
  background: var(--vp-surface);
}

.um-input.is-invalid {
  border-color: var(--vp-coral);
}

.um-error {
  font-size: 0.75rem;
  color: #c1291a;
  margin-top: 5px;
}

/* Reset-password callout */
.um-reset {
  background: rgba(var(--vp-accent-rgb), 0.08);
  border: 1px solid rgba(var(--vp-accent-rgb), 0.28);
  border-radius: 11px;
  padding: 13px 15px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.um-reset-text {
  min-width: 0;
  flex: 1 1 220px;
}

.um-reset-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #0b5e7d;
}

.um-reset-note {
  font-size: 0.78rem;
  color: #2e7b9c;
  margin-top: 2px;
  text-wrap: pretty;
}

.um-reset-btn {
  border: 1px solid var(--vp-accent);
  background: var(--vp-surface);
  color: #0b7fa8;
  border-radius: 9px;
  padding: 8px 14px;
  font-size: 0.79rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  flex: 0 0 auto;
}

.um-reset-btn:hover:not(:disabled) {
  background: var(--vp-gray-100);
}

.um-reset-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

/* Active switch */
.um-toggle-row {
  display: flex;
  align-items: center;
  gap: 11px;
  border: 1px solid var(--vp-gray-200);
  background: #fbfcfe;
  border-radius: 11px;
  padding: 11px 13px;
  text-align: left;
  width: 100%;
}

.um-switch {
  width: 40px;
  height: 23px;
  border-radius: 12px;
  background: var(--vp-gray-400);
  position: relative;
  flex-shrink: 0;
  transition: background 0.15s;
}

.um-switch.is-on {
  background: var(--vp-accent);
}

.um-switch i {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 42, 79, 0.3);
  transition: left 0.15s;
}

.um-switch.is-on i {
  left: 20px;
}

.um-toggle-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--vp-heading-color);
}

.um-toggle-note {
  display: block;
  font-size: 0.76rem;
  color: var(--vp-gray-500);
  text-wrap: pretty;
}

.um-foot {
  padding: 13px 20px;
  border-top: 1px solid var(--vp-gray-200);
  display: flex;
  gap: 9px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.um-btn {
  font-size: 0.84rem;
  border-radius: 10px;
  border: 1px solid var(--vp-gray-300);
  padding: 9px 16px;
  font-weight: 600;
  background: var(--vp-surface);
  color: var(--vp-body-color);
  white-space: nowrap;
  flex: 0 0 auto;
}

.um-btn:hover:not(:disabled) {
  background: var(--vp-gray-200);
}

.um-btn.is-primary {
  border: 0;
  padding: 9px 20px;
  font-weight: 700;
  background: var(--vp-accent);
  color: #fff;
}

.um-btn.is-primary:hover:not(:disabled) {
  background: var(--vp-info);
}

.um-btn:disabled {
  opacity: 0.65;
  cursor: default;
}
</style>
